export * from './components';
export * from '@omegagrid/tree';
export * from '@omegagrid/toolbar';
export * from '@reporting/rp-app-host';

import { ComponentId } from "@omegagrid/core";
import rp, { AppComponent, Application, Container, parseComponentId } from '@reporting/rp-app-host';
import { componentFactories } from './factories';
import { WikiSearchProvider } from './searchProvider';
import { findPageByName, getSidebar, pageComponentId, parseWikiPath, safeDecode } from './helpers';
import { PageContainer } from './components';
import { WikiTypes } from './types';

export class WikiApplication extends Application {

	/** the anchor of the address the page was opened with, scrolled to once the page it names opens (openPath) */
	private initialAnchor = '';

	async register(container: Container) {
		super.register(container);
		container.services.register('wiki');
		container.commands.registerProvider(new WikiSearchProvider());
		// the router rewrites the address without its fragment right away, so the anchor is taken here
		this.initialAnchor = safeDecode(container.router.initialAnchor);
		// a guest sees one page at a time: back/forward between pages (their addresses are set by PageContainer)
		if (container.router.anonymousAccess) {
			container.router.addEventListener('path', () => this.openPath(container.router.path.join('/')));
		}
	}

	createComponent(id: ComponentId): AppComponent|Promise<AppComponent> {
		const parsedId = parseComponentId(id);
		const factory = componentFactories.get(parsedId.id as string || parsedId.type);
		if (!factory) return null;
		return factory(this, parsedId);
	}

	/**
	 * Opens the page an address names (the addresses buildWikiPageUrl makes): `custom/<path>` is a page
	 * of the company wiki of the current company, anything else a page of the application wiki.
	 */
	async openPath(path: string) {
		if (!path) return;
		const { a, name } = parseWikiPath(path, rp.accountingProvider.a ?? 0);
		if (a && rp.userProvider.isGuest()) return; // a guest has no company wiki
		const page = await findPageByName(a, name);
		if (!page) return;
		if (a) getSidebar()?.showWiki(WikiTypes.custom);
		const container = await this.host.open<PageContainer>(pageComponentId(a, page.id_wiki_page), {
			title: page.name,
		});
		// the anchor belongs to the page the address named, not to the pages back/forward move to later
		const anchor = this.initialAnchor;
		this.initialAnchor = '';
		if (anchor) container?.scrollToAnchor(anchor);
	}
}
